import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { Container } from "./styles";
import api from "../../../../services/api";
import { IAnexo } from "../../../../utility/IAnexo";
import { useState } from "react";
import toast from "react-hot-toast";
import { toastDefsError } from "../../../../config/toastConfig";
import CardAnexo from "./CardAnexo";
import CardAnexoUpload from "./CardAnexoUpload";
import Skeleton from "react-loading-skeleton";
import { FileUploadInfo } from "../BlocoAdicionaAnexo";
import { formatByteSize } from "../../../../utility/byteSizeUtils";
import Select from "react-select";
import { comum } from "../../../../styles/Themes/light";

export interface BlocoListaAnexosProps {
    id_proposta?: number;
    loading?: boolean;
    setLoading: (loading: boolean) => void;
    uploadingFiles: FileUploadInfo[];
    anexos: IAnexo[];
    setAnexos: React.Dispatch<React.SetStateAction<IAnexo[]>>;
    onRetry?: (index: number) => Promise<void>;
    onCancelUpload?: (index: number) => void;
    highlightedFileId?: number | null;
}

export default function BlocoListaAnexos({ id_proposta, loading, setLoading, uploadingFiles, anexos, setAnexos, onRetry, onCancelUpload, highlightedFileId }: BlocoListaAnexosProps) {

    const [running, setRunning] = useState(false);
    const [filtroOrigem, setFiltroOrigem] = useState<string>('todas');
    const listaAnexosRef = useRef<HTMLDivElement>(null);

    const origensDisponiveis = useMemo(() => {
        const origens = new Set<string>();
        anexos.forEach(anexo => {
            if (anexo.origem) {
                origens.add(anexo.origem);
            }
        });
        return Array.from(origens).sort();
    }, [anexos]);

    const opcoesOrigem = useMemo(() => {
        const opcoes = [{ value: 'todas', label: 'Todas as origens' }];
        origensDisponiveis.forEach(origem => {
            opcoes.push({ value: origem, label: origem });
        });
        return opcoes;
    }, [origensDisponiveis]);

    const selectedOption = useMemo(() => {
        return opcoesOrigem.find(opcao => opcao.value === filtroOrigem) || opcoesOrigem[0];
    }, [opcoesOrigem, filtroOrigem]);

    const handleFiltroChange = useCallback((selected: any) => {
        setFiltroOrigem(selected?.value || 'todas');
    }, []);

    const selectStyles = useMemo(() => ({
        control: (provided: Record<string, unknown>, state: any) => ({
            ...provided,
            height: 48,
            minHeight: 48,
            boxShadow: "none",
            cursor: 'pointer',
            border: '1px solid #EAEEF3',
            '&:hover': {
                border: '1px solid #CCD6DB',
            },
        }),
        placeholder: (provided: Record<string, unknown>) => ({
            ...provided,
            color: 'var(--c353a40)',
            fontSize: '16px',
            fontWeight: '400',
        }),
        singleValue: (provided: Record<string, unknown>) => ({
            ...provided,
            color: 'var(--c353a40)',
            fontSize: '16px',
        }),
        option: (provided: Record<string, unknown>, state: any) => ({
            ...provided,
            color: 'var(--c5a646e)',
            fontSize: '16px',
            fontWeight: '500',
            backgroundColor: state.isSelected 
                ? 'var(--cf8f9fa)' 
                : state.isFocused 
                    ? 'var(--cf8f9fa)' 
                    : 'var(--cffffff)',
            '&:active': {
                backgroundColor: 'var(--cf8f9fa)',
            },
        }),
        menu: (provided: Record<string, unknown>) => ({
            ...provided,
            zIndex: 1000,
        }),
    }), []);

    const selectTheme = useCallback((theme: any) => ({
        ...theme,
        colors: {
            ...theme.colors,
            primary25: 'var(--cf8f9fa)',
            primary50: 'var(--cf8f9fa)',
            primary75: 'var(--cf8f9fa)',
            primary: comum.colors.primaryRed400,
            neutral0: 'var(--cffffff)',
            neutral5: 'var(--cf8f9fa)',
            neutral10: 'var(--cf8f9fa)',
            neutral20: 'var(--ceaeef3)',
            neutral30: 'var(--cccd6db)',
            neutral40: 'var(--c9ca3af)',
            neutral50: 'var(--c6b7280)',
            neutral60: 'var(--c5a646e)',
            neutral70: 'var(--c4b5563)',
            neutral80: 'var(--c374151)',
            neutral90: 'var(--c1f2937)',
        },
    }), []);

    const anexosFiltrados = useMemo(() => {
        // Primeiro filtra anexos excluídos (não devem aparecer na aba Anexos)
        const anexosNaoExcluidos = anexos.filter(anexo => !anexo.excluido);
        
        if (filtroOrigem === 'todas') {
            return anexosNaoExcluidos;
        }
        return anexosNaoExcluidos.filter(anexo => anexo.origem === filtroOrigem);
    }, [anexos, filtroOrigem]);

    useEffect(() => {
        if (filtroOrigem !== 'todas' && anexosFiltrados.length === 0 && anexos.length > 0) {
            setFiltroOrigem('todas');
        }
    }, [filtroOrigem, anexosFiltrados.length, anexos.length]);

    const totalAnexos = useMemo(() => {
        return anexos.filter(anexo => !anexo.excluido).length;
    }, [anexos]);

    const totalAnexosFiltrados = useMemo(() => {
        return anexosFiltrados.length;
    }, [anexosFiltrados]);

    const tamanhoTotal = useMemo(() => {
        return formatByteSize(anexosFiltrados.reduce(
            (acc, anexo) => acc + (anexo.tamanho || 0), 0)
           );
    }, [anexosFiltrados]);

    async function buscarAnexos() {
        setRunning(true);
        try {
            setLoading(true);
            const response = await api.get(`proposta/${id_proposta}/anexos`);
            setAnexos(response.data.result);
            setLoading(false);
        } catch (error) {
            toast.error('Erro ao buscar anexos', toastDefsError);
            setLoading(false);
        }
        setRunning(false);
    }

    useEffect(() => {
        if(id_proposta && loading && !running) {
            buscarAnexos();
        }
    }, [id_proposta, loading]);

    const scrollToAnexo = useCallback((idAnexo: number) => {
        if (!listaAnexosRef.current) return false;

        const elemento = listaAnexosRef.current.querySelector(`[data-anexo-id="${idAnexo}"]`) as HTMLElement;
        
        if (elemento) {
            elemento.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            elemento.style.transition = 'all 0.3s ease';
            elemento.style.border = '2px solid rgba(229, 41, 64, 0.5)';
            elemento.style.borderRadius = '8px';
            elemento.style.backgroundColor = 'rgba(229, 41, 64, 0.1)';
            elemento.style.padding = '8px';
            elemento.style.margin = '-8px';
            
            setTimeout(() => {
                elemento.style.border = '';
                elemento.style.backgroundColor = '';
                elemento.style.padding = '';
                elemento.style.margin = '';
                setTimeout(() => {
                    elemento.style.transition = '';
                }, 300);
            }, 3000);
            
            return true;
        }
        
        return false;
    }, []);

    useEffect(() => {
        if (!loading && anexosFiltrados.length > 0 && highlightedFileId) {
            let tentativas = 0;
            const MAX_TENTATIVAS = 10;
            
            const tentarScroll = () => {
                tentativas++;
                const sucesso = scrollToAnexo(highlightedFileId);
                if (!sucesso && tentativas < MAX_TENTATIVAS) {
                    setTimeout(tentarScroll, 100);
                }
            };
            
            const timeoutId = setTimeout(tentarScroll, 300);
            
            return () => clearTimeout(timeoutId);
        }
    }, [loading, anexosFiltrados, highlightedFileId, scrollToAnexo]);

    if(totalAnexos === 0 && uploadingFiles.length === 0) {
        return (
            <Container>
                <div className="header">
                    <div className="header-left">
                        <h2>Arquivos Anexados (0)</h2>
                        <p>0 B total</p>
                    </div>
                </div>
                <div className="lista-anexos">
                    <div className="lista-vazia">
                        <p>Nenhum anexo disponível</p>
                    </div>
                </div>
            </Container>
        );
    }

    return (
        <Container>
            <div className="header">
                <div className="header-left">
                    <h2>Arquivos Anexados ({filtroOrigem === 'todas' ? totalAnexos : `${totalAnexosFiltrados} de ${totalAnexos}`})</h2>
                    <p>{tamanhoTotal} total</p>
                </div>
                {opcoesOrigem.length > 1 && (
                    <div className="filtro-origem">
                        <label htmlFor="filtro-origem-select">Filtrar por origem:</label>
                        <Select
                            id="filtro-origem-select"
                            value={selectedOption}
                            onChange={handleFiltroChange}
                            options={opcoesOrigem}
                            styles={selectStyles}
                            theme={selectTheme}
                            isSearchable={false}
                            className="select-origem"
                            classNamePrefix="select-origem"
                        />
                    </div>
                )}
            </div>
            <div className="lista-anexos" ref={listaAnexosRef}>
            {/* Show uploading files first */}
            {uploadingFiles.map((fileInfo, idx) => (
                <CardAnexoUpload 
                    key={`uploading-${idx}`} 
                    fileInfo={fileInfo}
                    onRetry={() => onRetry?.(idx)}
                    onCancel={() => onCancelUpload?.(idx)}
                />
            ))}
            
            {/* Show loading skeletons or actual files */}
            {loading ? (
                <>
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="skeleton-card">
                            <Skeleton circle width={25} height={25} />
                            <div className="skeleton-meio">
                                <div className="skeleton-top">
                                    <Skeleton width={120} height={13} />
                                    <Skeleton width={40} height={18} borderRadius={12} />
                                </div>
                                <div className="skeleton-bottom">
                                    <Skeleton width={200} height={11} />
                                </div>
                            </div>
                            <Skeleton circle width={25} height={25} />
                        </div>
                    ))}
                </>
            ) : (
                anexosFiltrados.length > 0 ? (
                    anexosFiltrados.map((anexo) => (
                    <CardAnexo 
                        key={anexo.id_proposta_arquivo} 
                        anexo={anexo} 
                        highlighted={highlightedFileId === anexo.id_proposta_arquivo}
                        id_proposta={id_proposta}
                        onExcluir={() => {
                            setAnexos(prev => prev.filter(a => a.id_proposta_arquivo !== anexo.id_proposta_arquivo));
                            buscarAnexos();
                        }}
                    />
                ))
                ) : (
                    uploadingFiles.length === 0 && (
                        <div className="lista-vazia">
                            <p>{filtroOrigem === 'todas' ? 'Nenhum anexo encontrado' : `Nenhum anexo encontrado com origem "${opcoesOrigem.find(o => o.value === filtroOrigem)?.label}"`}</p>
                        </div>
                    )
                )
            )}
            </div>
        </Container>
    );
}